import {
  Badge,
  Box,
  Group,
  ScrollArea,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { CountStepper } from '../../components/CountStepper';
import { WhatThisMeans } from '../../components/WhatThisMeans';
import {
  coachCapacityStatus,
  coachMaxGroups,
  coachRequestLabel,
  coachSlotCounts,
  coachSlotMarkers,
  coachSlots,
  groupsRange,
  listLabel,
  plural,
  slotLabel,
} from '../../model/derive';
import { useSetup } from '../../SetupContext';
import classes from './DataStep.module.css';

export function CoachCapacityTab() {
  const { session, config, update, readOnly } = useSetup();
  const n = session.participants.length;
  const counts = coachSlotCounts(session.coaches, session.slots, config);
  const status = coachCapacityStatus(session.coaches, session.slots, config, n);
  const groups = groupsRange(n, config.groupSize);
  const least = counts.length > 0 ? Math.min(...counts) : 0;
  const leastSlots = session.slots.filter((_, i) => counts[i] === least);
  const awaiting = session.coaches.filter(c => c.awaitingResponse).length;
  const anyPartial = session.coaches.some(c =>
    Object.values(c.slotResponses).includes('partial')
  );
  const anyUnavailable = session.coaches.some(c =>
    Object.values(c.slotResponses).includes('unavailable')
  );
  const anyNotRequested = session.coaches.some(c =>
    session.slots.some(s => coachSlotMarkers(c, s).notRequested)
  );

  return (
    <Stack gap="md">
      <div>
        <Title order={4}>Coach capacity by time</Title>
        <Text size="sm" c="dimmed" maw={760} mt={6}>
          Availability comes from SGOS. Turn a dot off if a coach can't run a
          group at that time. Then set the most groups each coach can take.
        </Text>
      </div>
      {(anyPartial || anyUnavailable || anyNotRequested || awaiting > 0) && (
        <Group gap="lg" aria-label="Key">
          {anyPartial && (
            <Group gap={6}>
              <span
                className={classes.slotSwitch}
                data-partial
                aria-checked="true"
                aria-hidden
              />
              <Text size="xs" c="dimmed">
                Partial: the coach can make this time on some dates, not all
              </Text>
            </Group>
          )}
          {anyUnavailable && (
            <Group gap={6}>
              <span
                className={classes.slotSwitch}
                data-unavailable
                aria-checked="false"
                aria-hidden
              />
              <Text size="xs" c="dimmed">
                Unavailable: the coach said they can't make this time
              </Text>
            </Group>
          )}
          {anyNotRequested && (
            <Group gap={6}>
              <span className={classes.notRequestedSwatch} aria-hidden />
              <Text size="xs" c="dimmed">
                Not requested: the coach wasn't asked about this time
              </Text>
            </Group>
          )}
          {awaiting > 0 && (
            <Group gap={6}>
              <Badge color="warningAmber" variant="light" size="xs">
                No response
              </Badge>
              <Text size="xs" c="dimmed">
                The coach hasn't answered the availability request
              </Text>
            </Group>
          )}
        </Group>
      )}
      {session.coaches.length === 0 ? (
        <Text size="sm" c="dimmed">
          This matching session has no coach data, so coach capacity isn't
          enforced.
        </Text>
      ) : (
        <ScrollArea type="auto">
          <Table withTableBorder miw={860} verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={160}>Coach</Table.Th>
                {session.slots.map(s => (
                  <Table.Th key={s} ta="center">
                    {slotLabel(s)}
                  </Table.Th>
                ))}
                <Table.Th w={200} ta="center">
                  Most groups
                </Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {session.coaches.map(c => {
                const slots = coachSlots(c, config);
                const max = coachMaxGroups(c, config);
                const label = max === null ? 'No limit' : plural(max, 'group');
                // No cap already means one group per available slot, so
                // stepping up to the slot count goes back to "No limit".
                const setMax = (next: number | null) =>
                  update({
                    type: 'setCoachMaxGroups',
                    coachId: c.id,
                    maxGroups: next,
                  });
                return (
                  <Table.Tr key={c.id}>
                    <Table.Td>
                      <Text size="sm" fw={500}>
                        {c.name}
                      </Text>
                      {coachRequestLabel(c) && (
                        <Badge
                          color={c.awaitingResponse ? 'warningAmber' : 'gray'}
                          variant="light"
                          size="xs"
                          mt={4}
                        >
                          {coachRequestLabel(c)}
                        </Badge>
                      )}
                    </Table.Td>
                    {session.slots.map(s => {
                      const { partial, unavailable, notRequested } =
                        coachSlotMarkers(c, s);
                      const notes = [
                        partial && 'coach said partial',
                        unavailable && 'coach said unavailable',
                        notRequested && 'not requested',
                      ].filter(Boolean);
                      const tip = partial
                        ? 'The coach said they can make this time on some dates, not all.'
                        : unavailable
                          ? "The coach said they can't make this time."
                          : notRequested
                            ? "The coach wasn't asked about this time."
                            : null;
                      return (
                        <Table.Td
                          key={s}
                          ta="center"
                          className={
                            notRequested ? classes.notRequested : undefined
                          }
                        >
                          <Stack gap={2} align="center">
                            <Tooltip label={tip} disabled={!tip} withArrow>
                              <button
                                type="button"
                                role="switch"
                                aria-checked={slots.includes(s)}
                                aria-label={[
                                  `${c.name}, ${slotLabel(s)}`,
                                  ...notes,
                                ].join(', ')}
                                className={classes.slotSwitch}
                                data-partial={partial || undefined}
                                data-unavailable={unavailable || undefined}
                                disabled={readOnly}
                                onClick={() =>
                                  update({
                                    type: 'toggleCoachSlot',
                                    coachId: c.id,
                                    slot: s,
                                  })
                                }
                              />
                            </Tooltip>
                            {partial && (
                              <Text size="10px" fw={600} c="warningAmber.7">
                                Partial
                              </Text>
                            )}
                            {unavailable && (
                              <Text size="10px" fw={600} c="dangerRed.6">
                                Unavailable
                              </Text>
                            )}
                            {notRequested && (
                              <Text size="10px" c="dimmed">
                                Not requested
                              </Text>
                            )}
                          </Stack>
                        </Table.Td>
                      );
                    })}
                    <Table.Td>
                      {readOnly ? (
                        <Text ta="center" size="sm">
                          {label}
                        </Text>
                      ) : (
                        <CountStepper
                          value={label}
                          valueWidth={70}
                          decrementLabel="Fewer groups"
                          incrementLabel="More groups"
                          decrementDisabled={max === 0}
                          incrementDisabled={max === null}
                          onDecrement={() =>
                            setMax(Math.max(0, (max ?? slots.length) - 1))
                          }
                          onIncrement={() =>
                            setMax(
                              max !== null && max + 1 < slots.length
                                ? max + 1
                                : null
                            )
                          }
                        />
                      )}
                      {c.respondedMaxGroups !== null && (
                        // What the coach said, so it stays visible after the cap is changed.
                        <Text
                          size="10px"
                          ta="center"
                          mt={4}
                          fw={max !== c.respondedMaxGroups ? 600 : undefined}
                          c={
                            max !== c.respondedMaxGroups
                              ? 'warningAmber.7'
                              : 'dimmed'
                          }
                        >
                          Coach said {plural(c.respondedMaxGroups, 'group')}
                        </Text>
                      )}
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
            <Table.Tfoot>
              <Table.Tr bg="gray.0">
                <Table.Th>Coaches available</Table.Th>
                {counts.map((count, i) => (
                  <Table.Th
                    key={session.slots[i]}
                    ta="center"
                    c={count === 0 ? 'dangerRed.6' : undefined}
                  >
                    {count}
                  </Table.Th>
                ))}
                <Table.Th ta="center">{status.total} groups</Table.Th>
              </Table.Tr>
            </Table.Tfoot>
          </Table>
        </ScrollArea>
      )}
      {session.coaches.length > 0 && (
        <>
          <Box maw={760}>
            <WhatThisMeans
              lines={[
                `Total capacity is ${status.total} groups. Your group size requirement needs ${groups.label}.`,
                ...(awaiting > 0
                  ? [
                      `${plural(awaiting, 'coach', 'coaches')} ${awaiting === 1 ? "hasn't" : "haven't"} responded to the availability request, so they start with no times. Turn on times you've confirmed another way.`,
                    ]
                  : []),
                ...(session.coaches.some(
                  c => coachMaxGroups(c, config) === null
                )
                  ? [
                      'A coach with No limit can take one group at each timeslot they are available.',
                    ]
                  : []),
                ...(leastSlots.length > 0
                  ? [
                      `Timeslots with least coverage (${plural(least, 'coach', 'coaches')}): ${listLabel(leastSlots.map(slotLabel))}.`,
                    ]
                  : []),
              ]}
              callout={{ tone: status.level, text: status.message }}
            />
          </Box>
        </>
      )}
      {session.coaches.length > 0 && !config.enforce.coachCapacity && (
        <Group>
          <Text size="sm" c="dimmed">
            Coach capacity isn't enforced. You can turn it on in group
            requirements.
          </Text>
        </Group>
      )}
    </Stack>
  );
}
